(() => {
  "use strict";

  if (window.__nvsTorqueBasicLogicV3) return;
  window.__nvsTorqueBasicLogicV3 = true;
  const instances = new WeakMap();

  const ERROR_LABELS = {
    REMOVAL_GEOMETRY_REQUIRED: "Phép tính siết này không lưu đường kính mặt tỳ và bộ dữ liệu chưa có hình học mặc định tương ứng. Cần bổ sung hai đường kính hoặc dữ liệu tham chiếu để tính tháo.",
    CATALOG_STALE: "Danh mục đã thay đổi. Vui lòng chọn lại theo dữ liệu mới.",
    CATALOG_NOT_AVAILABLE: "Danh mục Basic chưa sẵn sàng. Vui lòng thử lại sau.",
    RELEASE_NOT_AVAILABLE: "Dữ liệu hiện chưa đủ điều kiện phát hành. Vui lòng thử lại sau.",
    OUTSIDE_VALIDATED_RANGE: "Tổ hợp đã chọn chưa nằm trong phạm vi dữ liệu được phép tính.",
    INVALID_INPUT: "Thông tin lựa chọn không hợp lệ. Vui lòng chọn lại.",
    REMOVAL_NOT_AVAILABLE: "Dữ liệu tháo hiện chưa được phép sử dụng.",
    REMOVAL_DATA_NOT_AVAILABLE: "Chưa có dữ liệu tháo phù hợp với lựa chọn này.",
    CALCULATION_ERROR: "Không thể hoàn tất và lưu phép tính. Vui lòng thử lại."
  };
  function escapeHtml(value) {
    return String(value ?? "").replace(/[&<>"']/g, char => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]));
  }
  function numberValue(value) {
    return value == null || typeof value === "boolean" || String(value).trim() === "" ? NaN : Number(value);
  }


  const COARSE_PITCH = {1.6:.35,1.8:.35,2:.4,2.2:.45,2.5:.45,3:.5,3.5:.6,4:.7,4.5:.75,5:.8,6:1,7:1,8:1.25,10:1.5,12:1.75,14:2,16:2,18:2.5,20:2.5,22:2.5,24:3,27:3,30:3.5,33:3.5,36:4,39:4,42:4.5,45:4.5,48:5};
  function threadFamily(row) {
    if (!row) return null;
    if (["metric_coarse","metric_fine","UNC","UNF"].includes(row.thread_family)) return row.thread_family;
    if(row.system === "metric" && ["coarse","fine"].includes(row.pitch_series)) return "metric_"+row.pitch_series;
    if(row.system === "imperial" && row.thread_profile === "Unified") return row.pitch_series === "coarse" ? "UNC" : row.pitch_series === "fine" ? "UNF" : null;
    // Legacy Worker: recognize only the explicit existing coarse series, not every metric ID.
    if(String(row.id).startsWith("metric-") && COARSE_PITCH[Number(row.nominal_diameter_mm)] === Number(row.pitch_mm)) return "metric_coarse";
    return null;
  }
  function threadLabel(row) {
    const family=threadFamily(row);
    if(family === "UNC" || family === "UNF") {
      const tpi=numberValue(row.threads_per_inch) || 25.4/Number(row.pitch_mm);
      return `${row.designation || row.id} (${fmt(tpi,0,2)} TPI)`;
    }
    return `M${fmt(row.nominal_diameter_mm,0,2)} × ${fmt(row.pitch_mm,0,2)} mm`;
  }

  const STATUS_LABELS = {
    valid: "Hợp lệ",
    warning: "Có cảnh báo",
    incomplete_provenance: "Thiếu thông tin nguồn",
    missing_assumption: "Thiếu giả định",
    outside_validated_range: "Ngoài phạm vi đã kiểm chứng",
    invalid_input: "Dữ liệu đầu vào không hợp lệ",
    calculation_error: "Lỗi tính toán"
  };

  const WARNING_LABELS = {
    GENERIC_K_ASSUMPTION: "Hệ số K giả định",
    USER_DEFINED_FRICTION: "Hệ số ma sát do người dùng nhập",
    USER_DEFINED_PRELOAD: "Lực siết trước do người dùng nhập",
    STAINLESS_GALLING_RISK: "Nguy cơ dính ren thép không gỉ",
    OUTSIDE_VALIDATED_FRICTION_RANGE: "Ngoài phạm vi ma sát đã kiểm chứng",
    OUTSIDE_VALIDATED_DIAMETER_RANGE: "Ngoài phạm vi đường kính đã kiểm chứng",
    NON_PREFERRED_FRICTION_PAIRING: "Tổ hợp ma sát không ưu tiên",
    SOURCE_TORQUE_DEVIATION_ABOVE_2_PERCENT: "Sai lệch trên 2% — chấp nhận kèm ghi chú",
    MANUFACTURER_REFERENCE_SCOPE: "Phạm vi tham khảo nhà sản xuất",
    REFERENCE_ONLY_RESULT: "Kết quả tham khảo kỹ thuật"
  };

  const REMOVAL_CONDITION_LABELS = {
    "removal-condition-new-clean": "Mới / sạch",
    "removal-condition-used-dry": "Đã sử dụng / khô",
    "removal-condition-light-corrosion": "Rỉ nhẹ",
    "removal-condition-moderate-corrosion": "Rỉ vừa",
    "removal-condition-heavy-corrosion-seizure": "Rỉ nặng / nghi kẹt"
  };

  const REMOVAL_WARNING_LABELS = {
    REFERENCE_ONLY_RESULT: "Kết quả tham khảo kỹ thuật",
    SERVICE_CONDITION_ESTIMATE: "Ước tính theo tình trạng sử dụng",
    LITERATURE_INFORMED_RANGE: "Khoảng ước tính tham khảo tài liệu",
    NPOWER_ASSUMPTION_USED: "Có sử dụng giả định NPOWER",
    RESIDUAL_PRELOAD_ESTIMATED: "Lực siết trước còn lại là giá trị ước tính",
    REMOVAL_FRICTION_ESTIMATED: "Ma sát khi tháo là giá trị ước tính",
    ENVIRONMENT_ALLOWANCE_ESTIMATED: "Ảnh hưởng môi trường là giá trị ước tính",
    PREVAILING_TORQUE_INCLUDED: "Đã cộng mô-men tự hãm",
    THREADLOCKER_BREAKAWAY_INCLUDED: "Đã cộng mô-men keo khóa ren",
    LOW_CONFIDENCE_ESTIMATE: "Độ tin cậy thấp",
    SEVERE_CORROSION_HIGH_UNCERTAINTY: "Độ bất định rất cao do rỉ nặng / nghi kẹt",
    INCOMPLETE_PROVENANCE: "Thiếu thông tin nguồn"
  };

  const CONFIDENCE_LABELS = {
    high: "Cao",
    medium: "Trung bình",
    low: "Thấp",
    very_low: "Rất thấp"
  };

  const BADGE_GUIDE = {
    standard: {
      label: "Theo tiêu chuẩn",
      tone: "standard"
    },
    reference: {
      label: "Tham chiếu",
      tone: "reference"
    },
    method: {
      label: "Phương pháp tính",
      tone: "method"
    },
    dataset: {
      label: "Bộ dữ liệu",
      tone: "dataset"
    },
    equation_basis: {
      label: "Cơ sở phương trình",
      tone: "standard"
    },
    reference_geometry: {
      label: "Hình học tham chiếu",
      tone: "reference"
    },
    reference_state: {
      label: "Trạng thái tham chiếu",
      tone: "reference"
    },
    npower: {
      label: "NPOWER giả định",
      tone: "assumption"
    },
    k_assumption: {
      label: "Hệ số K giả định",
      tone: "assumption"
    },
    condition_dependent: {
      label: "Phụ thuộc điều kiện sử dụng",
      tone: "condition"
    },
    reference_result: {
      label: "Kết quả tham khảo",
      tone: "reference"
    },
    confidence_high: {
      label: "Độ tin cậy cao",
      tone: "standard"
    },
    confidence_medium: {
      label: "Độ tin cậy trung bình",
      tone: "reference"
    },
    confidence_low: {
      label: "Độ tin cậy thấp",
      tone: "assumption"
    },
    confidence_very_low: {
      label: "Độ tin cậy rất thấp",
      tone: "critical"
    },
    severe_uncertainty: {
      label: "Độ bất định rất cao",
      tone: "critical"
    },
    galling: {
      label: "Nguy cơ dính ren",
      tone: "critical"
    }
  };

  function badgeHtml(key, label = "") {
    const badge = BADGE_GUIDE[key] || {
      label: label || key,
      tone: "dataset"
    };

    return `<span class="nvs-tool__badge" data-tone="${badge.tone}">${escapeHtml(label || badge.label)}</span>`;
  }

  function sourceTypeTone(type) {
    if (type === "Cơ sở tiêu chuẩn") return "standard";
    if (type === "Nguồn tham chiếu") return "reference";
    if (type === "Giả định NPOWER") return "assumption";
    if (type === "Phương pháp tính") return "method";
    if (type === "Cơ sở phương trình") return "standard";
    if (type === "Hình học tham chiếu") return "reference";
    if (type === "Trạng thái tham chiếu") return "reference";
    if (type === "Bộ dữ liệu") return "dataset";
    return "dataset";
  }

  function parseConfig(root) {
    try {
      return JSON.parse(root.querySelector("[data-nvs-tool-config]")?.textContent || "{}");
    } catch {
      return {};
    }
  }

  function materialClassLabel(row) {
    if (!row) return "";
    const propertyClass = String(row.property_class || "").trim();
    const id = String(row.id || "").trim();
    const sae=id.match(/^sae-j429-grade-(5|8)$/);
    if(sae)return `SAE Grade ${sae[1]}`;
    const match = id.match(/stainless-([A-Z]\d?)-?(\d+)?/i);

    if (match) {
      const grade = match[1].toUpperCase();
      const cls = propertyClass || match[2] || "";
      return cls ? `${grade}-${cls}` : grade;
    }

    return propertyClass || row.name || id;
  }

  function getArray(payload, paths) {
    for (const path of paths) {
      const value = path.split(".").reduce((obj, key) => obj?.[key], payload);
      if (Array.isArray(value)) return value;
    }
    return [];
  }

  function fmt(value, min = 2, max = 3) {
    const number = numberValue(value);
    if (!Number.isFinite(number)) return "—";
    return number.toLocaleString("vi-VN", {
      minimumFractionDigits: min,
      maximumFractionDigits: max
    });
  }

  class NvsTorqueBasic {
    constructor(root) {
      this.root = root;
      this.config = parseConfig(root);
      this.apiBase = String(
        this.config.apiBaseUrl ||
        root.dataset.apiBase ||
        "https://api.npower.com.vn"
      ).replace(/\/+$/, "");

      this.state = {
        family: "metric_coarse", allThreads: [],
        threads: [],
        propertyClasses: [],
        preloadPolicies: [],
        frictionProfiles: [],
        lastResult: null,
        lastRemovalResult: null
      };

      this.el = {
        families: Array.from(root.querySelectorAll('[data-thread-family]')),
        familyStatus: root.querySelector('[data-role="family-status"]'),
        geometryMissing: root.querySelector('[data-role="removal-geometry-missing"]'),
        geometryOpen: root.querySelector('[data-role="removal-geometry-open"]'),
        geometryFields: root.querySelector('[data-role="removal-geometry-fields"]'),
        removalInner: root.querySelector('[data-role="removal-bearing-inner"]'),
        removalOuter: root.querySelector('[data-role="removal-bearing-outer"]'),
        removalAvailability: root.querySelector('[data-role="removal-availability"]'),
        thread: root.querySelector('[name="thread_series_id"]'),
        cls: root.querySelector('[name="material_property_class_id"]'),
        preload: root.querySelector('[name="target_preload_policy_id"]'),
        friction: root.querySelector('[name="friction_profile_id"]'),
        calculate: root.querySelector(".nvs-tool__calculate"),
        reset: root.querySelector(".nvs-tool__reset"),
        message: root.querySelector('[data-role="message"]'),
        result: root.querySelector('[data-role="result"]'),
        torque: root.querySelector('[data-role="torque"]'),
        preloadResult: root.querySelector('[data-role="preload-result"]'),
        details: root.querySelector('[data-role="details"]'),
        steps: root.querySelector('[data-role="steps"]'),
        sources: root.querySelector('[data-role="sources"]'),
        basicWarnings: root.querySelector('[data-role="basic-warnings"]'),
        basicBadges: root.querySelector('[data-role="basic-badges"]'),
        copy: root.querySelector(".nvs-tool__copy"),
        share: root.querySelector(".nvs-tool__share"),
        shareMenu: root.querySelector('[data-role="share-menu"]'),
        shareStatus: root.querySelector('[data-role="share-status"]'),
        copyPreview: root.querySelector('[data-role="copy-preview"]'),
        trace: root.querySelector('[data-role="trace"]'),

        removalModule: root.querySelector('[data-role="removal-module"]'),
        removalToggle: root.querySelector('[data-role="removal-toggle"]'),
        removalControls: root.querySelector('[data-role="removal-controls"]'),
        removalConditions: Array.from(
          root.querySelectorAll('[data-removal-condition], [name="removal_service_condition_id"]')
        ),
        removalCalculate: root.querySelector(".nvs-tool__removal-calculate"),
        removalMessage: root.querySelector('[data-role="removal-message"]'),
        removalResult: root.querySelector('[data-role="removal-result"]'),
        removalTheoretical: root.querySelector('[data-role="removal-theoretical"]'),
        removalBreakaway: root.querySelector('[data-role="removal-breakaway"]'),
        removalBadges: root.querySelector('[data-role="removal-badges"]'),
        removalSteps: root.querySelector('[data-role="removal-steps"]'),
        removalCopy: root.querySelector(".nvs-tool__removal-copy"),
        removalShare: root.querySelector(".nvs-tool__removal-share"),
        removalShareMenu: root.querySelector('[data-role="removal-share-menu"]'),
        removalShareStatus: root.querySelector('[data-role="removal-share-status"]'),
        removalCopyPreview: root.querySelector('[data-role="removal-copy-preview"]'),
        removalAssumptions: root.querySelector('[data-role="removal-assumptions"]'),
        removalWarnings: root.querySelector('[data-role="removal-warnings"]'),
        removalSources: root.querySelector('[data-role="removal-sources"]'),
        removalTrace: root.querySelector('[data-role="removal-trace"]')
      };

      this.bound = false;
      this.destroyed = false;
      this.requests = new Map();
      this.lifetime = new AbortController();
      this.calculateLabel = this.el.calculate.textContent;
      this.removalCalculateLabel = this.el.removalCalculate?.textContent || "Tính ước tính tháo";
      this.state.validatedKey = null;
    }

    selection() {
      return {
        thread_series_id: this.el.thread.value,
        material_property_class_id: this.el.cls.value,
        target_preload_policy_id: this.el.preload.value,
        friction_profile_id: this.el.friction.value
      };
    }

    selectionKey() { return JSON.stringify(this.selection()); }

    cancelRequest(name) {
      const request = this.requests.get(name);
      if (request) request.controller.abort();
      this.requests.delete(name);
    }

    beginRequest(name) {
      this.cancelRequest(name);
      const request = { controller: new AbortController() };
      this.requests.set(name, request);
      return request;
    }

    isCurrent(name, request) {
      return !this.destroyed && this.requests.get(name) === request;
    }

    finishRequest(name, request) {
      if (this.isCurrent(name, request)) this.requests.delete(name);
    }

    invalidateSelection() {
      this.cancelRequest("validation");
      this.cancelRequest("calculation");
      this.state.validatedKey = null;
      this.hideMessage();
      this.hideResult();
      this.updateCalculateState();
    }

    rememberMetadata(payload) {
      this.state.datasetVersion = payload.dataset_version || this.state.datasetVersion;
      this.state.engineVersion = payload.engine_version || this.state.engineVersion;
    }

    notifySelect(select, cascade = false) {
      // Keep the theme's element-select in sync without generating duplicate API chains.
      this.syncingSelect = !cascade;
      try { select.dispatchEvent(new Event("change", { bubbles: true })); }
      finally { this.syncingSelect = false; }
      this.syncSelectVisual(select);
    }

    async loadDependent(name, select, query, responseKey, stateKey, config, emptyMessage) {
      if (this.state.catalog) {
        const column = {property_classes:1, preload_policies:2, friction_profiles:3}[responseKey];
        const allowed = new Set(this.catalogMatches(query).map(row => row[column]));
        this.state[stateKey] = this.state.catalog.options[responseKey].filter(row => allowed.has(row.id));
        this.setSelect(select, this.state[stateKey], config);
        if (!this.state[stateKey].length) this.showMessage(emptyMessage, "info");
        this.updateCalculateState();
        return;
      }
      const request = this.beginRequest(name);
      this.setSelectDisabled(select, true, "Đang tải...");
      this.updateCalculateState();
      try {
        const payload = await this.api("/api/engineering/torque/basic/compatible-options?" + new URLSearchParams(query), { signal: request.controller.signal });
        if (!this.isCurrent(name, request)) return;
        this.rememberMetadata(payload);
        this.state[stateKey] = getArray(payload, ["compatible_options." + responseKey]);
        this.setSelect(select, this.state[stateKey], config);
        if (!this.state[stateKey].length) this.showMessage(emptyMessage, "info");
      } catch (error) {
        if (!this.isCurrent(name, request)) return;
        this.setSelectDisabled(select, true, "Không thể tải dữ liệu");
        this.showMessage(error.message, "error");
      } finally { this.finishRequest(name, request); this.updateCalculateState(); }
    }

    async onFrictionChange() {
      this.invalidateSelection();
      const selection = this.selection();
      if (!Object.values(selection).every(Boolean) || !this.selected(this.state.frictionProfiles, selection.friction_profile_id)) return;
      const key = this.selectionKey();
      if (this.state.catalog) {
        if (this.catalogMatches(selection).length === 1) this.state.validatedKey = key;
        else this.showMessage("Tổ hợp này chưa có dữ liệu Basic Torque được phê duyệt.", "info");
        this.updateCalculateState();
        return;
      }
      const request = this.beginRequest("validation");
      this.updateCalculateState();
      try {
        const payload = await this.api("/api/engineering/torque/basic/compatible-options?" + new URLSearchParams(selection), { signal: request.controller.signal });
        if (!this.isCurrent("validation", request) || key !== this.selectionKey()) return;
        this.rememberMetadata(payload);
        if (payload.validation?.valid_combination === true && payload.validation?.matched_basic_torque_reference_id) {
          this.state.validatedKey = key;
        } else this.showMessage("Tổ hợp này chưa có dữ liệu Basic Torque được phê duyệt. Vui lòng chọn lại.", "info");
      } catch (error) {
        if (this.isCurrent("validation", request)) this.showMessage(error.message, "error");
      } finally { this.finishRequest("validation", request); this.updateCalculateState(); }
    }

    async api(path, options = {}) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      let timedOut = false;
      options.signal?.addEventListener("abort", abort, { once: true });
      if (options.signal?.aborted) controller.abort();
      const timer = setTimeout(() => { timedOut = true; controller.abort(); }, 30000);
      try {
        const response = await fetch(this.apiBase + path, {
          ...options, signal: controller.signal,
          headers: { Accept: "application/json", ...(options.body ? { "Content-Type": "application/json" } : {}), ...(options.headers || {}) }
        });
        const payload = await response.json();
        if (!response.ok || !payload || payload.ok !== true) {
          const error = new Error(ERROR_LABELS[payload?.error?.code] || payload?.error?.message || "Không thể hoàn tất yêu cầu.");
          error.code = payload?.error?.code;
          error.status = response.status;
          throw error;
        }
        return payload;
      } catch (error) {
        if (options.signal?.aborted) throw new DOMException("Request cancelled", "AbortError");
        if (timedOut) throw new Error("Dịch vụ phản hồi quá lâu. Vui lòng thử lại.");
        if (error instanceof SyntaxError) throw new Error("Dịch vụ trả về dữ liệu không hợp lệ. Vui lòng thử lại.");
        if (error instanceof TypeError) throw new Error("Không thể kết nối tới dịch vụ tính toán.");
        throw error;
      } finally {
        clearTimeout(timer);
        options.signal?.removeEventListener("abort", abort);
      }
    }

    setSelect(select, rows, config = {}) {
      const { placeholder = "Chọn một giá trị", valueField = "id", labelField = "name", labelFormatter = null } = config;
      select.replaceChildren(new Option(placeholder, ""));
      (rows || []).forEach(row => {
        const label = labelFormatter ? labelFormatter(row) : (row[labelField] || row.designation || row.property_class || row.id);
        select.add(new Option(label, row[valueField]));
      });
      select.disabled = !rows?.length;
      select.closest("element-select")?.classList.toggle("element-select__wrapper--disabled", select.disabled);
      if (rows?.length === 1) select.value = String(rows[0][valueField]);
      this.notifySelect(select, true);
    }

    setSelectDisabled(select, disabled, text = null) {
      if (text != null) select.replaceChildren(new Option(text, ""));
      select.disabled = disabled;
      select.closest("element-select")?.classList.toggle("element-select__wrapper--disabled", disabled);
      this.notifySelect(select);
    }

    selected(rows, id) {
      return rows.find((row) => String(row.id) === String(id)) || null;
    }

    syncSelectVisual(select) {
      if (!select) return;

      const wrapper = select.closest("element-select");
      if (!wrapper) return;

      const buttonText = wrapper.querySelector(
        ".element-button .element-text, .element-button__text, [data-button-text]"
      );

      if (!buttonText) return;

      const selectedOption = select.options[select.selectedIndex];

      if (selectedOption && selectedOption.value !== "") {
        buttonText.textContent = selectedOption.textContent;
      } else {
        buttonText.textContent =
          selectedOption?.textContent ||
          select.getAttribute("aria-label") ||
          "Chọn một giá trị";
      }
    }

    showMessage(message, type = "info") {
      this.el.message.hidden = false;
      this.el.message.dataset.type = type;
      this.el.message.textContent = message;
    }

    hideMessage() {
      this.el.message.hidden = true;
      this.el.message.textContent = "";
      delete this.el.message.dataset.type;
    }

    hideResult() {
      this.state.lastResult = null;
      if (this.el.copyPreview) this.el.copyPreview.value = "";
      this.toggleShareMenu(false);
      this.el.result.hidden = true;

      if (this.el.removalModule) {
        this.el.removalModule.hidden = true;
      }

      this.resetRemovalUi();
    }

    resetRemovalUi() {
      this.cancelRequest("removal-geometry");
      this.state.removalGeometryStatus=null;
      this.el.geometryOpen?.setAttribute("aria-expanded","false");
      if(this.el.geometryMissing)this.el.geometryMissing.hidden=true;
      if(this.el.geometryFields)this.el.geometryFields.hidden=true;
      if(this.el.removalAvailability)this.el.removalAvailability.hidden=true;
      [this.el.removalInner,this.el.removalOuter].forEach(el=>{if(el)el.value="";});
      if(this.el.removalToggle)this.el.removalToggle.disabled=false;
      this.cancelRequest("removal");
      if (this.el.removalCalculate) {
        this.el.removalCalculate.disabled = false;
        this.el.removalCalculate.textContent = this.removalCalculateLabel;
      }
      if (this.el.removalCopyPreview) this.el.removalCopyPreview.value = "";
      this.toggleRemovalShareMenu(false);
      this.state.lastRemovalResult = null;

      if (this.el.removalToggle) {
        this.el.removalToggle.checked = false;
      }

      if (this.el.removalControls) {
        this.el.removalControls.hidden = true;
      }

      if (this.el.removalResult) {
        this.el.removalResult.hidden = true;
      }

      if (this.el.removalMessage) {
        this.el.removalMessage.hidden = true;
        this.el.removalMessage.textContent = "";
        delete this.el.removalMessage.dataset.type;
      }

      this.el.removalConditions?.forEach((radio) => {
        radio.checked = radio.value === "removal-condition-light-corrosion";
      });
    }

    showRemovalMessage(message, type = "info") {
      if (!this.el.removalMessage) return;
      this.el.removalMessage.hidden = false;
      this.el.removalMessage.dataset.type = type;
      this.el.removalMessage.textContent = message;
    }

    hideRemovalMessage() {
      if (!this.el.removalMessage) return;
      this.el.removalMessage.hidden = true;
      this.el.removalMessage.textContent = "";
      delete this.el.removalMessage.dataset.type;
    }

    updateCalculateState() {
      const current = this.selection();
      const known = this.selected(this.state.threads, current.thread_series_id)
        && this.selected(this.state.propertyClasses, current.material_property_class_id)
        && this.selected(this.state.preloadPolicies, current.target_preload_policy_id)
        && this.selected(this.state.frictionProfiles, current.friction_profile_id);
      this.el.calculate.disabled = !known || this.state.validatedKey !== this.selectionKey()
        || ["root", "classes", "policies", "friction", "validation", "calculation"].some(key => this.requests.has(key));
      this.el.calculate.textContent = this.requests.has("calculation") ? "Đang tính..." : this.calculateLabel;
    }

    acceptCatalog(payload) {
      const fields = ["thread_series_id", "material_property_class_id", "target_preload_policy_id", "friction_profile_id", "basic_torque_reference_id"];
      const lists = ["threads", "property_classes", "preload_policies", "friction_profiles"];
      if (payload.schema_version !== 1 || typeof payload.catalog_revision !== "string" || !payload.catalog_revision
          || JSON.stringify(payload.combination_fields) !== JSON.stringify(fields)
          || !Array.isArray(payload.combinations) || !lists.every(key => Array.isArray(payload.options?.[key]))) {
        throw new Error("Danh mục Basic không đúng định dạng hỗ trợ.");
      }
      const ids = lists.map(key => new Set(payload.options[key].map(row => row.id)));
      if (!payload.combinations.every(row => Array.isArray(row) && row.length === 5 && row.every(value => typeof value === "string" && value.length)
          && row.slice(0,4).every((id,i) => ids[i].has(id)))) throw new Error("Danh mục Basic thiếu liên kết dữ liệu.");
      this.state.catalog = payload;
    }

    catalogMatches(selection) {
      const fields = this.state.catalog.combination_fields;
      return this.state.catalog.combinations.filter(row => fields.slice(0,4).every((field,i) => !selection[field] || row[i] === selection[field]));
    }


    applyFamilyFilter() {
      const family=this.state.family || "metric_coarse";
      this.state.threads=(this.state.allThreads || []).filter(row=>threadFamily(row)===family);
      this.el.families.forEach(input=>{
        input.checked=input.value===family;
        input.disabled=!(this.state.allThreads || []).some(row=>threadFamily(row)===input.value);
      });
      this.setSelect(this.el.thread,this.state.threads,{placeholder:"Chọn kích thước ren",labelFormatter:threadLabel});
      if(this.el.familyStatus) this.el.familyStatus.textContent=this.state.threads.length
        ? `${this.state.threads.length} cỡ ren có dữ liệu tính trong nhóm đã chọn.`
        : "Nhóm này chưa có dữ liệu tính khả dụng. Chọn nhóm khác hoặc bấm Đặt lại để tải lại.";
    }

    async onFamilyChange(value) {
      if(!this.el.families.some(input=>input.value===value && !input.disabled)) return;
      Array.from(this.requests.keys()).filter(key=>key!=="root").forEach(key=>this.cancelRequest(key));
      this.state.family=value;
      this.hideMessage();
      this.applyFamilyFilter();
      await this.onThreadChange();
    }

    removalSupported() {
      return !!this.state.lastResult?.calculation_id;
    }

    syncRemovalAvailability() {
      const supported=this.removalSupported();
      if(this.el.removalToggle) { this.el.removalToggle.disabled=!supported; if(!supported)this.el.removalToggle.checked=false; }
      if(this.el.removalCalculate)this.el.removalCalculate.disabled=!supported;
      if(!supported && this.el.removalControls)this.el.removalControls.hidden=true;
      if(this.el.removalAvailability) {
        this.el.removalAvailability.hidden=supported;
        this.el.removalAvailability.textContent=supported ? "" : "Đã có kết quả lực siết. Ước tính lực tháo chưa được hỗ trợ cho nhóm ren này trong phiên bản dữ liệu hiện tại.";
      }
    }

    async loadRoot() {
      this.state.catalog = null;
      const request = this.beginRequest("root");
      this.setSelectDisabled(this.el.thread, true, "Đang tải dữ liệu...");
      this.updateCalculateState();
      try {
        const payload = await this.api("/api/engineering/torque/basic/catalog", { signal:request.controller.signal, cache:"no-cache" });
        if (!this.isCurrent("root", request)) return;
        this.acceptCatalog(payload);
        this.rememberMetadata(payload);
        this.state.allThreads = payload.options.threads;
        this.applyFamilyFilter();
        if (!this.state.threads.length) this.showMessage("Hiện chưa có dữ liệu Basic Torque được phép tính.", "info");
      } catch (error) {
        if (!this.isCurrent("root", request)) return;
        // Old Workers remain supported during a rolling upgrade. Never bypass a release failure.
        if (error.status === 404) {
          this.finishRequest("root", request);
          return this.loadLegacyRoot();
        }
        this.state.threads = [];
        this.state.allThreads=[]; this.applyFamilyFilter();
        this.setSelectDisabled(this.el.thread, true, "Không thể tải dữ liệu");
        this.showMessage(error.message + " Bấm Đặt lại để tải lại.", "error");
      } finally { this.finishRequest("root", request); this.updateCalculateState(); }
    }

    async loadLegacyRoot() {
      const request = this.beginRequest("root");
      this.setSelectDisabled(this.el.thread, true, "Đang tải dữ liệu...");
      this.updateCalculateState();
      try {
        const payload = await this.api("/api/engineering/torque/basic/compatible-options", { signal: request.controller.signal });
        if (!this.isCurrent("root", request)) return;
        this.rememberMetadata(payload);
        this.state.allThreads = getArray(payload, ["compatible_options.threads"]);
        this.applyFamilyFilter();
        if (!this.state.threads.length) this.showMessage("Hiện chưa có dữ liệu Basic Torque được phép tính.", "info");
      } catch (error) {
        if (!this.isCurrent("root", request)) return;
        this.state.allThreads=[]; this.applyFamilyFilter();
        this.setSelectDisabled(this.el.thread, true, "Không thể tải dữ liệu");
        this.showMessage(error.message + " Bấm Đặt lại để tải lại.", "error");
      } finally { this.finishRequest("root", request); this.updateCalculateState(); }
    }

    async onThreadChange() {
      ["classes", "policies", "friction"].forEach(key => this.cancelRequest(key));
      this.invalidateSelection();
      this.state.propertyClasses = []; this.state.preloadPolicies = []; this.state.frictionProfiles = [];
      this.setSelectDisabled(this.el.cls, true, "Chọn kích thước ren trước");
      this.setSelectDisabled(this.el.preload, true, "Chọn cấp bền trước");
      this.setSelectDisabled(this.el.friction, true, "Chọn cơ sở lực siết trước");
      if (!this.selected(this.state.threads, this.el.thread.value)) return;
      await this.loadDependent("classes", this.el.cls, { thread_series_id: this.el.thread.value }, "property_classes", "propertyClasses", { placeholder: "Chọn cấp bền / cấp tính chất", labelFormatter: materialClassLabel }, "Chưa có cấp bền tương thích cho ren đã chọn.");
    }

    async onClassChange() {
      ["policies", "friction"].forEach(key => this.cancelRequest(key));
      this.invalidateSelection();
      this.state.preloadPolicies = []; this.state.frictionProfiles = [];
      this.setSelectDisabled(this.el.preload, true, "Chọn cấp bền trước");
      this.setSelectDisabled(this.el.friction, true, "Chọn cơ sở lực siết trước");
      if (!this.el.thread.value || !this.selected(this.state.propertyClasses, this.el.cls.value)) return;
      await this.loadDependent("policies", this.el.preload, { thread_series_id: this.el.thread.value, material_property_class_id: this.el.cls.value }, "preload_policies", "preloadPolicies", { placeholder: "Chọn cơ sở lực siết trước" }, "Tổ hợp này chưa có cơ sở lực siết trước được phê duyệt cho Basic Torque.");
    }

    async onPreloadChange() {
      this.cancelRequest("friction");
      this.invalidateSelection();
      this.state.frictionProfiles = [];
      this.setSelectDisabled(this.el.friction, true, "Chọn cơ sở lực siết trước");
      if (!this.el.thread.value || !this.el.cls.value || !this.selected(this.state.preloadPolicies, this.el.preload.value)) return;
      await this.loadDependent("friction", this.el.friction, { thread_series_id: this.el.thread.value, material_property_class_id: this.el.cls.value, target_preload_policy_id: this.el.preload.value }, "friction_profiles", "frictionProfiles", { placeholder: "Chọn điều kiện ma sát" }, "Chưa có hệ số K tương thích với tổ hợp này trong Basic Torque.");
    }

    renderDetails(payload) {
      const thread = this.selected(this.state.threads, this.el.thread.value);
      const cls = this.selected(this.state.propertyClasses, this.el.cls.value);
      const preload = this.selected(this.state.preloadPolicies, this.el.preload.value);
      const friction = this.selected(this.state.frictionProfiles, this.el.friction.value);
      const result = payload.result || {};

      const rows = [
        ["Ren / Thread designation", thread ? threadLabel(thread) : this.el.thread.value],
        ["Đường kính danh nghĩa / Nominal diameter, d", thread?.nominal_diameter_mm != null ? `${fmt(thread.nominal_diameter_mm, 0, 3)} mm` : "—"],
        ["Bước ren / Thread pitch, P", thread?.pitch_mm != null ? `${fmt(thread.pitch_mm, 0, 3)} mm` : "—"],
        ["Cấp bền / Property class", materialClassLabel(cls)],
        ["Nhóm vật liệu / Material family", cls?.material_family || payload.engineering_basis?.material_family || "—"],
        ["Cơ sở lực siết trước / Target preload policy", preload?.name || this.el.preload.value],
        ["Cấu hình ma sát / Friction profile", friction?.name || this.el.friction.value],
        ["Hệ số K / Nut factor", result.nut_factor_k ?? friction?.nut_factor_k ?? "—"],
        ["Lực siết trước / Preload", result.target_preload_kn != null ? `${fmt(result.target_preload_kn)} kN` : "—"],
        ["Mô-men siết / Tightening torque", result.tightening_torque_nm != null ? `${fmt(result.tightening_torque_nm)} N·m` : "—"]
      ];

      this.el.details.innerHTML = rows.map(([label, value]) => `
        <div class="nvs-tool__detail">
          <span>${escapeHtml(label)}</span>
          <strong>${escapeHtml(value ?? "—")}</strong>
        </div>
      `).join("");
    }

    buildSteps(payload) {
      const thread = this.selected(this.state.threads, this.el.thread.value);
      const preload = this.selected(this.state.preloadPolicies, this.el.preload.value);
      const friction = this.selected(this.state.frictionProfiles, this.el.friction.value);
      const result = payload.result || {};

      const d = numberValue(thread?.nominal_diameter_mm);
      const F = numberValue(result.target_preload_kn) * 1000;
      const K = numberValue(result.nut_factor_k ?? friction?.nut_factor_k);
      const T = numberValue(result.tightening_torque_nm);

      return [
        {
          title: "Xác định hình học ren / Thread geometry",
          text: `Sử dụng ${thread ? threadLabel(thread) : this.el.thread.value}; đường kính danh nghĩa (nominal diameter, d) và bước ren (thread pitch, P).`,
          formula: `d = ${fmt(d, 0, 3)} mm; P = ${fmt(thread?.pitch_mm, 0, 3)} mm`
        },
        {
          title: "Xác định lực siết trước / Determine target preload",
          text: `Áp dụng cơ sở lực siết trước “${preload?.name || this.el.preload.value}”.`,
          formula: `Preload: F = ${fmt(F, 0, 0)} N = ${fmt(F / 1000)} kN`
        },
        {
          title: "Xác định hệ số K / Determine nut factor K",
          text: `Sử dụng cấu hình ma sát “${friction?.name || this.el.friction.value}”.`,
          formula: `Nut factor: K = ${fmt(K, 2, 3)}`
        },
        {
          title: "Tính mô-men siết / Calculate tightening torque",
          text: "Phương pháp Nut Factor sử dụng preload, nut factor và nominal diameter. Các số hiển thị đã làm tròn; kết quả giữ nguyên từ phép tính gốc.",
          formula: `T = K × F × d / 1000 ≈ ${fmt(K, 2, 3)} × ${fmt(F, 0, 0)} × ${fmt(d, 0, 3)} / 1000 ≈ ${fmt(T)} N·m`
        }
      ];
    }

    renderSteps(payload) {
      this.el.steps.innerHTML = this.buildSteps(payload).map((step, index) => `
        <article class="nvs-tool__step">
          <div class="nvs-tool__step-number">${index + 1}</div>
          <div>
            <h4>${escapeHtml(step.title)}</h4>
            <p>${escapeHtml(step.text)}</p>
            <div class="nvs-tool__formula">${escapeHtml(step.formula)}</div>
          </div>
        </article>
      `).join("");
    }

    sourceData(payload) {
      const preload = this.selected(this.state.preloadPolicies, this.el.preload.value);
      const friction = this.selected(this.state.frictionProfiles, this.el.friction.value);
      const trace = payload.trace || {};
      const rows = [
        { type: "Nguồn tham chiếu", name: preload?.name || payload.engineering_basis?.target_preload_policy_id || "Cơ sở lực siết trước",
          use: "Cơ sở lực siết trước dành cho đúng tổ hợp đã chọn. Tên policy không đồng nghĩa với chứng nhận tiêu chuẩn." },
        { type: friction?.source_classification === "npower_assumption" ? "Giả định NPOWER" : "Nguồn tham chiếu",
          name: friction?.name || payload.engineering_basis?.friction_profile_name || "Cấu hình hệ số K",
          use: `Hệ số K: ${resultValue(payload.result?.nut_factor_k)}. Phân loại nguồn: ${friction?.source_classification || "Chưa được API cung cấp"}.` },
        { type: "Phương pháp tính", name: trace.calculation_method_id || "Chưa được API cung cấp",
          use: `Phiên bản: ${trace.calculation_method_version || "—"}. Phương pháp tính mô-men tham khảo theo hệ số K.` },
        { type: "Bộ dữ liệu", name: `NPOWER Engineering Dataset ${trace.core_dataset_version || trace.dataset_version || "—"}`,
          use: `Engine: ${trace.engine_version || "—"}. Phiên bản dữ liệu dùng cho phép tính này.` }
      ];
      if (trace.evidence_pack_id) rows.push({ type: "Bộ dữ liệu", name: trace.evidence_pack_id, use: `Dataset pack: ${trace.evidence_pack_dataset_version || "—"}; anchor: ${trace.evidence_pack_anchor_id || "—"}.` });
      return rows;
    }

    renderSources(payload) {
      this.el.sources.innerHTML = this.sourceData(payload).map((source) => `
        <article class="nvs-tool__source">
          <div class="nvs-tool__source-head">
            <h4>${escapeHtml(source.name)}</h4>
            <span
              class="nvs-tool__source-type nvs-tool__badge"
              data-tone="${sourceTypeTone(source.type)}"
            >${escapeHtml(source.type)}</span>
          </div>
          <p>${escapeHtml(source.use)}</p>
        </article>
      `).join("");
    }

    renderWarnings(payload) {
      this.renderWarningList(this.el.basicWarnings, payload.warnings, WARNING_LABELS);
      if (!this.el.basicBadges) return;

      const warnings = Array.isArray(payload.warnings) ? payload.warnings : [];
      const friction = this.selected(
        this.state.frictionProfiles,
        this.el.friction.value
      );
      const badges = [];

      const push = (key, label = "") => {
        const html = badgeHtml(key, label);
        if (!badges.includes(html)) badges.push(html);
      };

      if (friction?.source_classification === "npower_assumption") {
        push("npower");
      }

      warnings.forEach((warning) => {
        if (warning.code === "GENERIC_K_ASSUMPTION") push("k_assumption");
        else if (warning.code === "REFERENCE_ONLY_RESULT") push("reference_result");
        else if (warning.code === "STAINLESS_GALLING_RISK") push("galling");
        else {
          push("reference_result", WARNING_LABELS[warning.code] || warning.title || warning.code);
        }
      });

      this.el.basicBadges.innerHTML = badges.join("");
      this.el.basicBadges.hidden = badges.length === 0;
    }

    buildCopyText(payload) {
      const thread = this.selected(this.state.threads, this.el.thread.value);
      const cls = this.selected(this.state.propertyClasses, this.el.cls.value);
      const preload = this.selected(this.state.preloadPolicies, this.el.preload.value);
      const friction = this.selected(this.state.frictionProfiles, this.el.friction.value);
      const result = payload.result || {};

      const lines = [
        "NPOWER ENGINEERING — KẾT QUẢ TÍNH MÔ-MEN SIẾT / BOLT TORQUE RESULT",
        "",
        "THÔNG SỐ / INPUTS",
        `Ren / Thread designation: ${thread ? threadLabel(thread) : this.el.thread.value}`,
        `Cấp bền / Property class: ${materialClassLabel(cls)}`,
        `Cơ sở lực siết trước / Target preload policy: ${preload?.name || this.el.preload.value}`,
        `Điều kiện ma sát / Friction profile: ${friction?.name || this.el.friction.value}`,
        `Hệ số K / Nut factor: ${result.nut_factor_k ?? friction?.nut_factor_k ?? "—"}`,
        "",
        "KẾT QUẢ / RESULT",
        `Lực siết trước / Preload: ${result.target_preload_kn ?? "—"} kN`,
        `Mô-men siết tham khảo / Reference tightening torque: ${result.tightening_torque_nm ?? "—"} N·m`,
        `Trạng thái / Status: ${STATUS_LABELS[payload.status] || payload.status}`,
        "",
        "CÁC BƯỚC TÍNH TOÁN / CALCULATION STEPS"
      ];

      this.buildSteps(payload).forEach((step, index) => {
        lines.push(`${index + 1}. ${escapeHtml(step.title)}`);
        lines.push(`   ${escapeHtml(step.text)}`);
        lines.push(`   ${escapeHtml(step.formula)}`);
      });

      lines.push("", "NGUỒN THAM KHẢO / REFERENCES");
      this.sourceData(payload).forEach((source, index) => {
        lines.push(`${index + 1}. [${escapeHtml(source.type)}] ${escapeHtml(source.name)}`);
        lines.push(`   ${escapeHtml(source.use)}`);
      });

      lines.push("", "CẢNH BÁO / WARNINGS");
      (payload.warnings || []).forEach((warning, index) => {
        lines.push(
          `${index + 1}. ${WARNING_LABELS[warning.code] || warning.title || warning.code}: ${escapeHtml(warning.message || "")}`
        );
      });


      lines.push(
        "",
        "TRUY VẾT / TRACE",
        `Calculation ID: ${payload.calculation_id || "—"}`,
        `Dataset version: ${payload.trace?.core_dataset_version || payload.trace?.dataset_version || "—"}`,
        ...(payload.trace?.evidence_pack_id ? [`Evidence pack: ${payload.trace.evidence_pack_id}`, `Pack dataset: ${payload.trace.evidence_pack_dataset_version || "—"}`, `Anchor: ${payload.trace.evidence_pack_anchor_id || "—"}`] : []),
        `Calculation method: ${payload.trace?.calculation_method_id || "—"} ${payload.trace?.calculation_method_version || ""}`.trim(),
        `Engine version: ${payload.trace?.engine_version || "—"}`,
        `Input hash: ${payload.trace?.input_hash || "—"}`,
        `Calculation hash: ${payload.trace?.calculation_hash || "—"}`,
        "",
        "LƯU Ý / DISCLAIMER",
        payload.disclaimer || this.config.labels?.disclaimer || ""
      );

      return lines.join("\n");
    }

    selectedRemovalConditionId() {
      return this.el.removalConditions?.find((radio) => radio.checked)?.value || "";
    }

    removalConditionLabel(id, fallback = "") {
      return REMOVAL_CONDITION_LABELS[id] || fallback || id || "—";
    }

    removalRangeText(range) {
      if (!range) return "Không định lượng";

      const low = numberValue(range.low_nm);
      const nominal = numberValue(range.nominal_nm);
      const high = numberValue(range.high_nm);

      if (![low, nominal, high].every(Number.isFinite)) {
        return "Không định lượng";
      }

      if (Math.abs(high - low) < 0.005) {
        return `${fmt(nominal)} N·m`;
      }

      return `${fmt(low)} – ${fmt(high)} N·m`;
    }

    buildRemovalSteps(payload) {
      const result = payload.result || {};
      const basis = payload.engineering_basis || {};
      const installation = basis.installation || {};
      const reference = basis.theoretical_reference || {};
      const geometry = basis.bearing_geometry || {};
      const service = basis.service_state || {};

      const targetPreloadN = numberValue(installation.target_preload_n);
      const pitchMm = numberValue(
        basis.thread_pitch_mm ??
        basis.pitch_mm ??
        geometry.pitch_mm
      );
      const pitchDiameterMm = numberValue(
        basis.thread_pitch_diameter_mm ??
        basis.pitch_diameter_mm ??
        geometry.thread_pitch_diameter_mm
      );
      const dKmMm = numberValue(geometry.effective_bearing_diameter_mm);
      const muThreadRef = numberValue(reference.mu_thread_remove);
      const muBearingRef = numberValue(reference.mu_bearing_remove);
      const theoreticalNm = numberValue(result.theoretical_removal_torque_nm);

      const steps = [
        {
          title: "Nhận trạng thái lắp đặt từ phép tính lực siết",
          text: "Công cụ dùng Basic Calculation ID để lấy lại lực siết trước và nhận dạng fastener đã lưu. Không suy ngược hệ số ma sát tháo từ hệ số K.",
          formula: Number.isFinite(targetPreloadN)
            ? `F_install = ${fmt(targetPreloadN, 0, 0)} N = ${fmt(targetPreloadN / 1000, 2, 3)} kN`
            : "F_install = —"
        },
        {
          title: "Xác định hình học tham chiếu khi tháo",
          text: "Sử dụng bước ren, đường kính trung bình ren và đường kính ma sát hiệu dụng của mặt tỳ đã được đăng ký cho cỡ ren hiện tại.",
          formula: [
            Number.isFinite(pitchMm) ? `P = ${fmt(pitchMm, 0, 3)} mm` : null,
            Number.isFinite(pitchDiameterMm) ? `d₂ = ${fmt(pitchDiameterMm, 0, 3)} mm` : null,
            Number.isFinite(dKmMm) ? `D_Km = ${fmt(dKmMm, 0, 3)} mm` : null
          ].filter(Boolean).join("; ") || "Geometry = —"
        },
        {
          title: "Tính lực tháo lý thuyết",
          text: "Dùng trạng thái tham chiếu với lực siết trước còn lại bằng lực lắp đặt và hệ số ma sát tháo tham chiếu độc lập với hệ số K.",
          formula: [
            `R_residual = ${fmt(reference.residual_preload_ratio ?? 1, 2, 3)}`,
            Number.isFinite(muThreadRef) ? `μ_thread = ${fmt(muThreadRef, 3, 4)}` : null,
            Number.isFinite(muBearingRef) ? `μ_bearing = ${fmt(muBearingRef, 3, 4)}` : null,
            Number.isFinite(theoreticalNm) ? `T_remove,theoretical = ${fmt(theoreticalNm)} N·m` : null
          ].filter(Boolean).join("; ")
        }
      ];

      if (result.output_mode === "not_quantified") {
        steps.push({
          title: "Đánh giá lực mở theo điều kiện sử dụng",
          text: "Điều kiện đã chọn có độ bất định quá cao nên công cụ không xuất một giá trị breakaway dạng số.",
          formula: "T_breakaway,service = Không định lượng"
        });
        return steps;
      }

      const scenarios = ["low", "nominal", "high"];
      const breakaway = result.service_breakaway || {};

      scenarios.forEach((scenario) => {
        const s = service?.[scenario];
        if (!s) return;

        const residualRatio = numberValue(s.residual_preload_ratio);
        const residualN =
          Number.isFinite(targetPreloadN) && Number.isFinite(residualRatio)
            ? targetPreloadN * residualRatio
            : null;

        const removeNm = numberValue(
          s.theoretical_service_removal_nm ??
          s.removal_torque_nm
        );

        const breakawayNm = numberValue(breakaway[`${scenario}_nm`]);

        steps.push({
          title: `Kịch bản ${scenario} — trạng thái sử dụng`,
          text: "Áp dụng tải siết trước còn lại, ma sát tháo theo tình trạng sử dụng và các thành phần cản bổ sung đã được cấu hình.",
          formula: [
            Number.isFinite(residualRatio) ? `R_residual = ${fmt(residualRatio, 3, 3)}` : null,
            Number.isFinite(residualN) ? `F_residual = ${fmt(residualN, 0, 0)} N` : null,
            Number.isFinite(numberValue(s.mu_thread_remove)) ? `μ_thread = ${fmt(s.mu_thread_remove, 3, 4)}` : null,
            Number.isFinite(numberValue(s.mu_bearing_remove)) ? `μ_bearing = ${fmt(s.mu_bearing_remove, 3, 4)}` : null,
            Number.isFinite(removeNm) ? `T_remove = ${fmt(removeNm)} N·m` : null,
            Number.isFinite(breakawayNm) ? `T_breakaway = ${fmt(breakawayNm)} N·m` : null
          ].filter(Boolean).join("; ") || "Scenario data = —"
        });
      });

      steps.push({
        title: "Tổng hợp khoảng lực mở dự đoán",
        text: "Kết quả cuối được trình bày dưới dạng khoảng low–high cùng giá trị nominal thay vì một mô-men duy nhất.",
        formula: this.removalRangeText(result.service_breakaway)
      });

      return steps;
    }

    renderRemovalSteps(payload) {
      if (!this.el.removalSteps) return;

      this.el.removalSteps.innerHTML = this.buildRemovalSteps(payload).map((step, index) => `
        <article class="nvs-tool__step">
          <div class="nvs-tool__step-number">${index + 1}</div>
          <div>
            <h4>${escapeHtml(step.title)}</h4>
            <p>${escapeHtml(step.text)}</p>
            <div class="nvs-tool__formula">${escapeHtml(step.formula)}</div>
          </div>
        </article>
      `).join("");
    }

    renderRemovalAssumptions(payload) {
      if (!this.el.removalAssumptions) return;

      const basis = payload.engineering_basis || {};
      const install = basis.installation || {};
      const ref = basis.theoretical_reference || {};
      const geometry = basis.bearing_geometry || {};
      const service = basis.service_state || {};
      const rows = [];

      rows.push([
        "Lực siết trước ban đầu / Initial preload",
        Number.isFinite(numberValue(install.target_preload_n))
          ? `${fmt(numberValue(install.target_preload_n) / 1000, 2, 3)} kN`
          : "—"
      ]);

      rows.push([
        "Ma sát ren khi tháo tham chiếu / Reference removal thread friction",
        Number.isFinite(numberValue(ref.mu_thread_remove))
          ? fmt(ref.mu_thread_remove, 3, 4)
          : "—"
      ]);

      rows.push([
        "Ma sát mặt tỳ khi tháo tham chiếu / Reference removal bearing friction",
        Number.isFinite(numberValue(ref.mu_bearing_remove))
          ? fmt(ref.mu_bearing_remove, 3, 4)
          : "—"
      ]);

      rows.push([
        "Đường kính ma sát hiệu dụng / Effective bearing diameter",
        Number.isFinite(numberValue(geometry.effective_bearing_diameter_mm))
          ? `${fmt(geometry.effective_bearing_diameter_mm, 3, 3)} mm`
          : "—"
      ]);

      if (payload.result?.output_mode === "bounded_range") {
        ["low", "nominal", "high"].forEach((scenario) => {
          const s = service?.[scenario];
          if (!s) return;

          const installN = numberValue(install.target_preload_n);
          const residualRatio = numberValue(s.residual_preload_ratio);
          const residualKn =
            Number.isFinite(installN) && Number.isFinite(residualRatio)
              ? (installN * residualRatio) / 1000
              : null;

          rows.push([
            `Lực siết trước còn lại — ${scenario}`,
            residualKn != null
              ? `${fmt(residualKn, 2, 3)} kN (${fmt(residualRatio * 100, 0, 1)}%)`
              : "—"
          ]);

          rows.push([
            `μ ren / μ mặt tỳ khi tháo — ${scenario}`,
            `${fmt(s.mu_thread_remove, 3, 4)} / ${fmt(s.mu_bearing_remove, 3, 4)}`
          ]);
        });

        const envModel =
          service?.nominal?.environment_allowance_model ||
          service?.low?.environment_allowance_model ||
          service?.high?.environment_allowance_model ||
          "none";

        rows.push([
          "Mô hình ảnh hưởng môi trường / Environmental allowance",
          envModel
        ]);
      } else {
        rows.push([
          "Trạng thái định lượng / Quantification",
          "Không định lượng do độ bất định quá cao."
        ]);
      }

      this.el.removalAssumptions.innerHTML = rows.map(([label, value]) => `
        <div class="nvs-tool__detail">
          <span>${escapeHtml(label)}</span>
          <strong>${value}</strong>
        </div>
      `).join("");
    }

    renderRemovalBadges(payload) {
      if (!this.el.removalBadges) return;

      const warnings = Array.isArray(payload.warnings) ? payload.warnings : [];
      const basis = payload.engineering_basis || {};
      const service = basis.service_state || {};
      const evidenceClass = String(
        service.evidence_class ||
        service.nominal?.evidence_class ||
        ""
      ).toLowerCase();
      const badges = [];

      const push = (key, label = "") => {
        const html = badgeHtml(key, label);
        if (!badges.includes(html)) badges.push(html);
      };

      // Every service breakaway estimate depends on the selected service condition.
      push("condition_dependent");

      if (evidenceClass.includes("npower") || evidenceClass.includes("assumption")) {
        push("npower");
      }

      const confidence = String(payload.result?.confidence || "").toLowerCase();
      if (confidence === "high") push("confidence_high");
      if (confidence === "medium") push("confidence_medium");
      if (confidence === "low") push("confidence_low");
      if (confidence === "very_low") push("confidence_very_low");

      warnings.forEach((warning) => {
        if (warning.code === "NPOWER_ASSUMPTION_USED") push("npower");
        else if (warning.code === "SEVERE_CORROSION_HIGH_UNCERTAINTY") push("severe_uncertainty");
        else if (warning.code === "LOW_CONFIDENCE_ESTIMATE") {
          if (!["low", "very_low"].includes(confidence)) push("confidence_low");
        }
        else if (warning.code === "REFERENCE_ONLY_RESULT") push("reference_result");
      });

      this.el.removalBadges.innerHTML = badges.join("");
      this.el.removalBadges.hidden = badges.length === 0;
    }

    renderWarningList(element, warnings, labels) {
      if (!element) return;
      // Badge meanings are explained in the dedicated section, not repeated in references.
      const badgeExplanationCodes = new Set([
        "GENERIC_K_ASSUMPTION",
        "REFERENCE_ONLY_RESULT",
        "SERVICE_CONDITION_ESTIMATE",
        "NPOWER_ASSUMPTION_USED"
      ]);
      const rows = (Array.isArray(warnings) ? warnings : [])
        .filter(warning => !badgeExplanationCodes.has(warning.code));
      element.hidden = !rows.length;
      element.innerHTML = rows.map(warning => `
        <article class="nvs-tool__warning" data-severity="${escapeHtml(warning.severity || "info")}">
          <strong>${escapeHtml(labels[warning.code] || warning.title || warning.code)}</strong>
          <div>${escapeHtml(warning.message || "")}</div>
          ${Number.isFinite(numberValue(warning.details?.deviation_percent)) ? `<div>Sai lệch: ${fmt(warning.details.deviation_percent)}%; mô-men nguồn: ${fmt(warning.details.source_torque_nm)} N·m; mô-men tính: ${fmt(warning.details.calculated_torque_nm)} N·m.</div>` : ""}
        </article>
      `).join("");
    }

    renderRemovalWarnings(payload) {
      this.renderWarningList(this.el.removalWarnings, payload.warnings, REMOVAL_WARNING_LABELS);
    }

    buildRemovalCopyText(payload) {
      if (!payload) return "";

      const result = payload.result || {};
      const basis = payload.engineering_basis || {};
      const trace = payload.trace || {};
      const installation = basis.installation || {};
      const reference = basis.theoretical_reference || {};
      const geometry = basis.bearing_geometry || {};
      const conditionId =
        basis.service_condition_id ||
        this.selectedRemovalConditionId() ||
        "";
      const warnings = Array.isArray(payload.warnings) ? payload.warnings : [];
      const lines = [];

      lines.push(
        "NPOWER ENGINEERING",
        "KẾT QUẢ LỰC THÁO / LỰC MỞ",
        "========================================",
        "",
        "1. KẾT QUẢ / RESULTS",
        `• Tình trạng / Service condition: ${this.removalConditionLabel(conditionId, basis.service_condition_name_vi)}`,
        `• Lực tháo lý thuyết / Theoretical removal torque: ${Number.isFinite(numberValue(result.theoretical_removal_torque_nm)) ? `${fmt(result.theoretical_removal_torque_nm)} N·m` : "—"}`,
        `• Lực mở dự đoán / Estimated service breakaway: ${result.output_mode === "not_quantified" ? "Không định lượng" : this.removalRangeText(result.service_breakaway)}`,
        `• Mức tin cậy / Confidence: ${CONFIDENCE_LABELS[result.confidence] || result.confidence || "—"}`,
        "",
        "2. TRẠNG THÁI LẮP ĐẶT / INSTALLATION STATE",
        `• Lực siết trước ban đầu / Initial preload: ${Number.isFinite(numberValue(installation.target_preload_n)) ? `${fmt(numberValue(installation.target_preload_n) / 1000, 2, 3)} kN` : "—"}`,
        `• Mô-men siết ban đầu / Tightening torque: ${Number.isFinite(numberValue(installation.tightening_torque_nm)) ? `${fmt(installation.tightening_torque_nm)} N·m` : "—"}`,
        `• Hệ số K / Nut factor: ${Number.isFinite(numberValue(installation.nut_factor_k)) ? fmt(installation.nut_factor_k, 2, 3) : "—"}`,
        "",
        "3. CƠ SỞ THAM CHIẾU / REFERENCE BASIS",
        `• μ ren khi tháo / Removal thread friction: ${Number.isFinite(numberValue(reference.mu_thread_remove)) ? fmt(reference.mu_thread_remove, 3, 4) : "—"}`,
        `• μ mặt tỳ khi tháo / Removal bearing friction: ${Number.isFinite(numberValue(reference.mu_bearing_remove)) ? fmt(reference.mu_bearing_remove, 3, 4) : "—"}`,
        `• D_Km / Effective bearing diameter: ${Number.isFinite(numberValue(geometry.effective_bearing_diameter_mm)) ? `${fmt(geometry.effective_bearing_diameter_mm, 3, 3)} mm` : "—"}`,
        "",
        "4. CẢNH BÁO / WARNINGS"
      );

      if (!warnings.length) {
        lines.push("• Không có cảnh báo bổ sung.");
      } else {
        warnings.forEach((warning) => {
          lines.push(`• ${escapeHtml(REMOVAL_WARNING_LABELS[warning.code] || warning.title || warning.code)}${warning.message ? ` — ${warning.message}` : ""}`);
        });
      }

      lines.push(
        "",
        "5. TRUY VẾT / TRACE",
        `• Basic Calculation ID: ${payload.basic_calculation_id || "—"}`,
        `• Removal Calculation ID: ${payload.calculation_id || "—"}`,
        `• Core dataset: ${trace.core_dataset_version || "—"}`,
        `• Removal dataset: ${trace.removal_dataset_version || "—"}`,
        `• Theoretical method: ${trace.theoretical_method_id || "—"}${trace.theoretical_method_version ? ` v${trace.theoretical_method_version}` : ""}`,
        `• Breakaway method: ${trace.breakaway_method_id || "—"}${trace.breakaway_method_version ? ` v${trace.breakaway_method_version}` : ""}`,
        `• Engine version: ${trace.engine_version || "—"}`,
        `• Runtime scope: ${trace.runtime_scope || "—"}`,
        "",
        "6. LƯU Ý / DISCLAIMER",
        "Kết quả lực tháo / lực mở là ước tính kỹ thuật phụ thuộc điều kiện sử dụng thực tế.",
        "Không sử dụng như giới hạn an toàn hoặc yêu cầu tháo lắp phổ quát."
      );

      return lines.join("\r\n");
    }

    removalShareSummary() {
      const payload = this.state.lastRemovalResult;
      if (!payload) return "";

      const result = payload.result || {};
      const basis = payload.engineering_basis || {};
      const conditionId = basis.service_condition_id || this.selectedRemovalConditionId();

      return [
        "NPOWER Engineering — Removal / Breakaway Estimate",
        `Tình trạng / Service condition: ${this.removalConditionLabel(conditionId, basis.service_condition_name_vi)}`,
        `Lực tháo lý thuyết / Theoretical removal: ${
          Number.isFinite(numberValue(result.theoretical_removal_torque_nm))
            ? `${fmt(result.theoretical_removal_torque_nm)} N·m`
            : "—"
        }`,
        `Lực mở / Breakaway: ${
          result.output_mode === "not_quantified"
            ? "Không định lượng"
            : this.removalRangeText(result.service_breakaway)
        }`,
        `Độ tin cậy / Confidence: ${CONFIDENCE_LABELS[result.confidence] || result.confidence || "—"}`,
        `Removal Calculation ID: ${payload.calculation_id || "—"}`
      ].join("\n");
    }

    async copyRemoval() {
      const payload = this.state.lastRemovalResult;
      if (!payload) return;

      const text = this.buildRemovalCopyText(payload);

      try {
        await navigator.clipboard.writeText(text);
        const previous = this.el.removalCopy?.textContent || "";
        if (this.el.removalCopy) this.el.removalCopy.textContent = "Đã sao chép";
        setTimeout(() => {
          if (this.el.removalCopy) {
            this.el.removalCopy.textContent = previous || "Copy kết quả lực mở";
          }
        }, 1500);
      } catch {
        if (!this.el.removalCopyPreview) return;
        this.el.removalCopyPreview.value = text;
        this.el.removalCopyPreview.hidden = false;
        this.el.removalCopyPreview.focus();
        this.el.removalCopyPreview.select();
        document.execCommand("copy");
        this.el.removalCopyPreview.hidden = true;
      }
    }

    setRemovalShareStatus(message) {
      if (!this.el.removalShareStatus) return;
      this.el.removalShareStatus.textContent = message || "";
      clearTimeout(this.removalShareStatusTimer);

      if (message) {
        this.removalShareStatusTimer = setTimeout(() => {
          if (this.el.removalShareStatus) this.el.removalShareStatus.textContent = "";
        }, 2800);
      }
    }

    toggleRemovalShareMenu(force) {
      if (!this.el.removalShareMenu) return;

      const open =
        typeof force === "boolean"
          ? force
          : this.el.removalShareMenu.hidden;

      this.el.removalShareMenu.hidden = !open;
      this.el.removalShare?.setAttribute("aria-expanded", open ? "true" : "false");
    }

    async shareRemovalNative() {
      const payload = this.state.lastRemovalResult;
      if (!payload) return;

      if (!navigator.share) {
        this.setRemovalShareStatus("Trình duyệt này không hỗ trợ System Share.");
        return;
      }

      try {
        await navigator.share({
          title: "NPOWER Engineering — Removal / Breakaway Result",
          text: this.removalShareSummary(),
          url: this.currentShareUrl()
        });
      } catch (error) {
        if (error?.name !== "AbortError") {
          this.setRemovalShareStatus("Không thể mở System Share.");
        }
      }
    }

    shareRemovalEmail() {
      const payload = this.state.lastRemovalResult;
      if (!payload) return;

      const subject = encodeURIComponent(
        "NPOWER Engineering — Kết quả lực tháo / lực mở"
      );

      const body = encodeURIComponent(
        `${this.removalShareSummary()}\n\n${this.buildRemovalCopyText(payload)}\n\nLink công cụ:\n${this.currentShareUrl()}`
      );

      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }

    shareRemovalWhatsApp() {
      const payload = this.state.lastRemovalResult;
      if (!payload) return;

      const text = encodeURIComponent(
        `${this.removalShareSummary()}\n\n${this.buildRemovalCopyText(payload)}\n\n${this.currentShareUrl()}`
      );

      this.openExternal(`https://wa.me/?text=${text}`);
    }

    async copyRemovalShareLink() {
      const url = this.currentShareUrl();

      try {
        await navigator.clipboard.writeText(url);
        this.setRemovalShareStatus("Đã sao chép liên kết.");
      } catch {
        const helper = document.createElement("textarea");
        helper.value = url;
        helper.setAttribute("readonly", "");
        helper.style.position = "fixed";
        helper.style.opacity = "0";
        document.body.appendChild(helper);
        helper.select();
        document.execCommand("copy");
        helper.remove();
        this.setRemovalShareStatus("Đã sao chép liên kết.");
      }
    }

    async handleRemovalShareOption(action) {
      this.toggleRemovalShareMenu(false);

      if (action === "native") await this.shareRemovalNative();
      if (action === "facebook") this.shareFacebook();
      if (action === "email") this.shareRemovalEmail();
      if (action === "whatsapp") this.shareRemovalWhatsApp();
      if (action === "copy-link") await this.copyRemovalShareLink();
    }

    removalSourceData(payload) {
      const basis = payload.engineering_basis || {};
      const trace = payload.trace || {};
      const geometry = basis.bearing_geometry || {};
      const reference = basis.theoretical_reference || {};

      return [
        {
          type: "Phương pháp tính",
          name: trace.theoretical_method_id || "theoretical-removal-torque-v1",
          use: `Phương pháp tính lực tháo lý thuyết${
            trace.theoretical_method_version
              ? ` — version ${trace.theoretical_method_version}`
              : ""
          }.`
        },
        {
          type: "Phương pháp tính",
          name: trace.breakaway_method_id || "service-breakaway-estimate-v1",
          use: `Phương pháp ước tính lực mở theo điều kiện sử dụng${
            trace.breakaway_method_version
              ? ` — version ${trace.breakaway_method_version}`
              : ""
          }.`
        },
        {
          type: geometry.geometry_basis_id === "unified-hex-cap-reference-assumption-v1" ? "Giả định NPOWER" : geometry.geometry_basis_id === "user-defined-annular-contact" ? "Người dùng nhập" : "Hình học tham chiếu",
          name: geometry.geometry_basis_id === "unified-hex-cap-reference-assumption-v1" ? "Mặt tỳ hex cap mặc định (giả định)" : geometry.geometry_basis_id === "user-defined-annular-contact" ? "Mặt tiếp xúc do người dùng nhập / User-defined bearing contact" : geometry.geometry_basis_id || geometry.basis_id || "Reference bearing geometry",
          use: Number.isFinite(numberValue(geometry.effective_bearing_diameter_mm))
            ? `Đường kính ma sát hiệu dụng D_Km = ${fmt(geometry.effective_bearing_diameter_mm, 3, 3)} mm.`
            : "Hình học mặt tỳ tham chiếu được lấy từ dataset Removal."
        },
        {
          type: "Trạng thái tham chiếu",
          name: reference.friction_parameter_set_id || "Reference removal friction state",
          use: "Hệ số ma sát tháo tham chiếu được lấy trực tiếp từ Removal dataset; không suy ngược từ hệ số K của phép tính lực siết."
        },
        {
          type: "Bộ dữ liệu",
          name: `NPOWER Removal Dataset ${trace.removal_dataset_version || "—"}`,
          use: `Core dataset: ${trace.core_dataset_version || "—"}. Removal model: ${trace.removal_model_version || "—"}.`
        }
      ];
    }

    renderRemovalSources(payload) {
      if (!this.el.removalSources) return;

      this.el.removalSources.innerHTML = this.removalSourceData(payload).map((source) => `
        <article class="nvs-tool__source">
          <div class="nvs-tool__source-head">
            <h4>${escapeHtml(source.name)}</h4>
            <span
              class="nvs-tool__source-type nvs-tool__badge"
              data-tone="${sourceTypeTone(source.type)}"
            >${escapeHtml(source.type)}</span>
          </div>
          <p>${escapeHtml(source.use)}</p>
        </article>
      `).join("");
    }

    renderRemovalResult(payload) {
      this.state.lastRemovalResult = payload;
      const result = payload.result || {};
      const basis = payload.engineering_basis || {};
      const conditionId =
        basis.service_condition_id ||
        this.selectedRemovalConditionId() ||
        "";

      if (this.el.removalTheoretical) {
        this.el.removalTheoretical.textContent =
          Number.isFinite(numberValue(result.theoretical_removal_torque_nm))
            ? `${fmt(result.theoretical_removal_torque_nm)} N·m`
            : "—";
      }

      if (this.el.removalBreakaway) {
        this.el.removalBreakaway.textContent =
          result.output_mode === "not_quantified"
            ? "Không định lượng"
            : this.removalRangeText(result.service_breakaway);
      }

      this.renderRemovalBadges(payload);
      this.renderRemovalSteps(payload);
      this.renderRemovalAssumptions(payload);
      this.renderRemovalWarnings(payload);
      this.renderRemovalSources(payload);

      if (this.el.removalTrace) {
        this.el.removalTrace.textContent = JSON.stringify({
          calculation_id: payload.calculation_id || null,
          basic_calculation_id: payload.basic_calculation_id || null,
          status: payload.status || null,
          result: payload.result || null,
          engineering_basis: payload.engineering_basis || null,
          trace: payload.trace || null
        }, null, 2);
      }

      if (this.el.removalCopyPreview) {
        this.el.removalCopyPreview.value = this.buildRemovalCopyText(payload);
      }

      if (this.el.removalResult) {
        this.el.removalResult.hidden = false;
      }
    }

    async loadRemovalGeometry() {
      const id=this.state.lastResult?.calculation_id;
      if(!id || this.config.enableRemovalEstimate===false || !this.el.removalModule)return;
      const req=this.beginRequest("removal-geometry");
      this.state.removalGeometryStatus="loading";
      if(this.el.removalCalculate)this.el.removalCalculate.disabled=true;
      if(this.el.geometryMissing)this.el.geometryMissing.hidden=true;
      if(this.el.geometryFields)this.el.geometryFields.hidden=true;
      try {
        const p=await this.api("/api/engineering/torque/basic/removal-geometry?basic_calculation_id="+encodeURIComponent(id),{signal:req.controller.signal,cache:"no-store"});
        if(!this.isCurrent("removal-geometry",req)||id!==this.state.lastResult?.calculation_id)return;
        if(p.basic_calculation_id!==id||typeof p.reference_available!=="boolean")throw new Error("Không thể xác định hình học tham chiếu.");
        this.state.removalGeometryStatus=p.reference_available?"reference":"missing";
        if(this.el.geometryMissing)this.el.geometryMissing.hidden=p.reference_available;
        this.hideRemovalMessage();
      } catch(e) {
        if(!this.isCurrent("removal-geometry",req))return;
        this.state.removalGeometryStatus="error";
        this.showRemovalMessage("Chưa tải được dữ liệu hình học. Bấm Tính lực tháo để thử lại.","error");
      } finally {
        if(this.isCurrent("removal-geometry",req)){
          this.finishRequest("removal-geometry",req);
          if(this.el.removalCalculate)this.el.removalCalculate.disabled=false;
        }
      }
    }

    async calculateRemoval() {
      if(!this.removalSupported()) {this.syncRemovalAvailability();return;}
      const basicCalculationId = this.state.lastResult?.calculation_id;
      const serviceConditionId = this.selectedRemovalConditionId();
      if (!basicCalculationId || !serviceConditionId || !this.el.removalToggle?.checked || this.config.enableRemovalEstimate === false || this.requests.has("removal")) return;
      if(!["reference","missing"].includes(this.state.removalGeometryStatus)) {
        if(this.state.removalGeometryStatus!=="loading")await this.loadRemovalGeometry();
        return;
      }
      const innerText=this.el.removalInner?.value?.trim() || "", outerText=this.el.removalOuter?.value?.trim() || "";
      const selectedThread=this.selected(this.state.threads,this.el.thread.value);

      let bearing_geometry;
      if(innerText || outerText) {
        const inner=numberValue(innerText.replace(",",".")),outer=numberValue(outerText.replace(",","."));
        if(!Number.isFinite(inner)||!Number.isFinite(outer)||inner<Number(selectedThread?.nominal_diameter_mm)||outer<=inner) {
          this.showRemovalMessage("Nhập đường kính mặt tiếp xúc thực tế bằng mm: ngoài > trong ≥ đường kính danh nghĩa của ren.","error"); return;
        }
        bearing_geometry={inner_contact_diameter_mm:inner,outer_contact_diameter_mm:outer};
      }
      this.invalidateRemoval();
      const request = this.beginRequest("removal");
      const button = this.el.removalCalculate;
      if (button) { button.disabled = true; button.textContent = "Đang ước tính..."; }
      try {
        const payload = await this.api("/api/engineering/torque/basic/removal-estimate", {
          method: "POST", signal: request.controller.signal,
          body: JSON.stringify({ basic_calculation_id: basicCalculationId, service_condition_id: serviceConditionId, ...(bearing_geometry ? {bearing_geometry} : {}) })
        });
        if (!this.isCurrent("removal", request) || basicCalculationId !== this.state.lastResult?.calculation_id || serviceConditionId !== this.selectedRemovalConditionId() || !this.el.removalToggle.checked) return;
        if (payload.basic_calculation_id !== basicCalculationId) throw new Error("Kết quả tháo không khớp với phép tính lực siết hiện tại.");
        this.renderRemovalResult(payload);
      } catch (error) {
        if (this.isCurrent("removal", request)) this.showRemovalMessage(error.message, "error");
      } finally {
        if (this.isCurrent("removal", request)) {
          this.finishRequest("removal", request);
          if (button) { button.disabled = false; button.textContent = this.removalCalculateLabel; }
        }
      }
    }

    invalidateRemoval() {
      this.cancelRequest("removal");
      this.state.lastRemovalResult = null;
      this.hideRemovalMessage();
      if (this.el.removalResult) this.el.removalResult.hidden = true;
      if (this.el.removalCopyPreview) this.el.removalCopyPreview.value = "";
      if (this.el.removalCalculate) { this.el.removalCalculate.disabled = false; this.el.removalCalculate.textContent = this.removalCalculateLabel; }
      this.toggleRemovalShareMenu(false);
    }

    renderResult(payload) {
      this.state.lastResult = payload;
      this.resetRemovalUi();

      if (this.el.removalModule) {
        this.el.removalModule.hidden = this.config.enableRemovalEstimate === false || !payload.calculation_id;
      }
      this.syncRemovalAvailability();
      this.loadRemovalGeometry();

      const result = payload.result || {};

      this.el.torque.textContent = fmt(result.tightening_torque_nm);
      this.el.preloadResult.textContent = result.target_preload_kn != null
        ? `${fmt(result.target_preload_kn)} kN`
        : "—";

      this.renderDetails(payload);
      this.renderSteps(payload);
      this.renderSources(payload);
      this.renderWarnings(payload);
      this.el.copyPreview.value = this.buildCopyText(payload);

      if (this.el.trace) {
        this.el.trace.textContent = JSON.stringify({
          calculation_id: payload.calculation_id || null,
          status: payload.status || null,
          engineering_basis: payload.engineering_basis || null,
          trace: payload.trace || null
        }, null, 2);
      }

      this.el.result.hidden = false;
    }

    async calculate() {
      this.updateCalculateState();
      if (this.el.calculate.disabled) return;
      const selection = this.selection();
      const key = this.selectionKey();
      this.hideMessage(); this.hideResult();
      const request = this.beginRequest("calculation");
      this.updateCalculateState();
      try {
        const payload = await this.api("/api/engineering/torque/basic", { method: "POST", signal: request.controller.signal, body: JSON.stringify({ ...selection, ...(this.state.catalog ? {catalog_revision:this.state.catalog.catalog_revision} : {}) }) });
        if (!this.isCurrent("calculation", request) || key !== this.selectionKey()) return;
        if (!Number.isFinite(numberValue(payload.result?.tightening_torque_nm)) || !payload.calculation_id) throw new Error("Kết quả trả về thiếu mô-men hoặc mã phép tính.");
        this.renderResult(payload);
      } catch (error) {
        if (!this.isCurrent("calculation", request)) return;
        if (error.code === "CATALOG_STALE") {
          await this.reset();
          this.showMessage("Danh mục đã cập nhật. Vui lòng chọn lại trước khi tính; chưa có phép tính mới được lưu.", "info");
          return;
        }
        if (error.code === "RELEASE_NOT_AVAILABLE" || error.code === "OUTSIDE_VALIDATED_RANGE") this.state.validatedKey = null;
        this.showMessage(error.message, "error");
      } finally { this.finishRequest("calculation", request); this.updateCalculateState(); }
    }
    async copyAll() {
      const text = this.el.copyPreview.value;

      try {
        await navigator.clipboard.writeText(text);
        const previous = this.el.copy.textContent;
        this.el.copy.textContent = "Đã sao chép";
        setTimeout(() => {
          this.el.copy.textContent = previous;
        }, 1500);
      } catch {
        this.el.copyPreview.focus();
        this.el.copyPreview.select();
        document.execCommand("copy");
      }
    }

    shareSummary() {
      const payload = this.state.lastResult;
      if (!payload) return "";

      const thread = this.selected(this.state.threads, this.el.thread.value);
      const cls = this.selected(this.state.propertyClasses, this.el.cls.value);
      const result = payload.result || {};

      const lines = [
        "NPOWER Engineering — Bolt Torque Calculator",
        `Ren / Thread: ${thread ? threadLabel(thread) : this.el.thread.value}`,
        `Cấp bền / Property class: ${materialClassLabel(cls)}`,
        `Mô-men siết tham khảo / Reference tightening torque: ${result.tightening_torque_nm ?? "—"} N·m`,
        `Lực siết trước / Target preload: ${result.target_preload_kn ?? "—"} kN`,
        `Calculation ID: ${payload.calculation_id || "—"}`
      ];

      return lines.join("\n");
    }

    currentShareUrl() {
      return window.location.href.split("#")[0];
    }

    setShareStatus(message) {
      this.el.shareStatus.textContent = message || "";
      clearTimeout(this.shareStatusTimer);
      if (message) {
        this.shareStatusTimer = setTimeout(() => {
          this.el.shareStatus.textContent = "";
        }, 2800);
      }
    }

    toggleShareMenu(force) {
      if (!this.el.shareMenu) return;
      const open = typeof force === "boolean" ? force : this.el.shareMenu.hidden;
      this.el.shareMenu.hidden = !open;
      this.el.share?.setAttribute("aria-expanded", open ? "true" : "false");
    }

    openExternal(url) {
      window.open(url, "_blank", "noopener,noreferrer,width=720,height=650");
    }

    async shareNative() {
      if (!this.state.lastResult) return;

      if (!navigator.share) {
        this.setShareStatus("Trình duyệt này không hỗ trợ System Share.");
        return;
      }

      try {
        await navigator.share({
          title: "NPOWER Engineering — Bolt Torque Result",
          text: this.shareSummary(),
          url: this.currentShareUrl()
        });
      } catch (error) {
        if (error?.name !== "AbortError") {
          this.setShareStatus("Không thể mở System Share.");
        }
      }
    }

    shareFacebook() {
      const url = encodeURIComponent(this.currentShareUrl());
      this.openExternal(`https://www.facebook.com/sharer/sharer.php?u=${url}`);
    }

    shareEmail() {
      const subject = encodeURIComponent(
        "NPOWER Engineering — Kết quả tính mô-men siết bu lông"
      );
      const body = encodeURIComponent(
        `${this.shareSummary()}\n\n${this.el.copyPreview.value}\n\nLink công cụ:\n${this.currentShareUrl()}`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }

    shareWhatsApp() {
      const text = encodeURIComponent(
        `${this.shareSummary()}\n\n${this.el.copyPreview.value}\n\n${this.currentShareUrl()}`
      );
      this.openExternal(`https://wa.me/?text=${text}`);
    }

    async copyShareLink() {
      const url = this.currentShareUrl();

      try {
        await navigator.clipboard.writeText(url);
        this.setShareStatus("Đã sao chép liên kết.");
      } catch {
        const helper = document.createElement("textarea");
        helper.value = url;
        helper.setAttribute("readonly", "");
        helper.style.position = "fixed";
        helper.style.opacity = "0";
        document.body.appendChild(helper);
        helper.select();
        document.execCommand("copy");
        helper.remove();
        this.setShareStatus("Đã sao chép liên kết.");
      }
    }

    async handleShareOption(action) {
      this.toggleShareMenu(false);

      if (action === "native") await this.shareNative();
      if (action === "facebook") this.shareFacebook();
      if (action === "email") this.shareEmail();
      if (action === "whatsapp") this.shareWhatsApp();
      if (action === "copy-link") await this.copyShareLink();
    }

    async reset() {
      this.state.family="metric_coarse"; this.state.allThreads=[];
      this.el.families.forEach(input=>{input.checked=input.value==="metric_coarse";input.disabled=true;});
      Array.from(this.requests.keys()).forEach(key => this.cancelRequest(key));
      this.state.catalog = null;
      this.state.threads = [];
      this.el.thread.value = "";
      this.syncSelectVisual(this.el.thread);
      this.onThreadChange();
      await this.loadRoot();
    }

    listen(target, type, listener) {
      target?.addEventListener(type, listener, { signal: this.lifetime.signal });
    }

    destroy() {
      this.destroyed = true;
      Array.from(this.requests.keys()).forEach(key => this.cancelRequest(key));
      this.lifetime.abort();
      clearTimeout(this.shareStatusTimer);
      clearTimeout(this.removalShareStatusTimer);
      delete this.root.dataset.nvsToolBound;
    }

    bindTabs() {
      this.root.querySelectorAll(".nvs-tool__tab").forEach((tab) => {
        tab.addEventListener("click", () => {
          this.root.querySelectorAll(".nvs-tool__tab")
            .forEach((item) => item.classList.remove("is-active"));
          this.root.querySelectorAll(".nvs-tool__panel")
            .forEach((item) => item.classList.remove("is-active"));

          tab.classList.add("is-active");
          this.root.querySelector(`[data-panel="${tab.dataset.tab}"]`)
            ?.classList.add("is-active");
        });
      });
    }

    init() {
      if (this.bound) return;
      this.bound = true;
      this.listen(this.el.geometryOpen,"click",()=>{
        if(this.state.removalGeometryStatus!=="missing")return;
        this.el.geometryFields.hidden=false;
        this.el.geometryOpen.setAttribute("aria-expanded","true");
        this.el.removalInner?.focus();
        this.hideRemovalMessage();
      });
      [this.el.removalInner,this.el.removalOuter].forEach(el=>this.listen(el,"input",()=>this.invalidateRemoval()));
      this.el.families.forEach(input=>{
        input.checked=input.value==="metric_coarse"; input.disabled=true;
        this.listen(input,"change",()=>{if(input.checked)this.onFamilyChange(input.value);});
      });

      this.listen(this.el.thread, "change", () => {
        if (this.syncingSelect) return;
        this.syncSelectVisual(this.el.thread);
        this.onThreadChange();
      });

      this.listen(this.el.cls, "change", () => {
        if (this.syncingSelect) return;
        this.syncSelectVisual(this.el.cls);
        this.onClassChange();
      });

      this.listen(this.el.preload, "change", () => {
        if (this.syncingSelect) return;
        this.syncSelectVisual(this.el.preload);
        this.onPreloadChange();
      });

      this.listen(this.el.friction, "change", () => {
        if (this.syncingSelect) return;
        this.syncSelectVisual(this.el.friction);
        this.onFrictionChange();
      });
      this.listen(this.el.calculate, "click", () => this.calculate());
      this.listen(this.el.reset, "click", () => this.reset());
      this.listen(this.el.copy, "click", () => this.copyAll());

      this.listen(this.el.removalToggle, "change", () => {
        this.invalidateRemoval();
        if (this.el.removalControls) this.el.removalControls.hidden = !this.el.removalToggle.checked || !this.removalSupported();
      });
      this.el.removalConditions?.forEach(radio => {
        this.listen(radio, "change", () => this.invalidateRemoval());
      });

      this.listen(this.el.removalCalculate, 
        "click",
        () => this.calculateRemoval()
      );

      this.listen(this.el.removalCopy, "click", () => this.copyRemoval());

      this.el.removalShare?.setAttribute("aria-haspopup", "menu");
      this.el.removalShare?.setAttribute("aria-expanded", "false");
      this.listen(this.el.removalShare, "click", (event) => {
        event.stopPropagation();
        this.toggleRemovalShareMenu();
      });

      this.el.removalShareMenu?.querySelectorAll("[data-removal-share]").forEach((button) => {
        this.listen(button, "click", () => {
          this.handleRemovalShareOption(button.dataset.removalShare);
        });
      });

      this.el.share?.setAttribute("aria-haspopup", "menu");
      this.el.share?.setAttribute("aria-expanded", "false");
      this.listen(this.el.share, "click", (event) => {
        event.stopPropagation();
        this.toggleShareMenu();
      });

      this.el.shareMenu?.querySelectorAll("[data-share]").forEach((button) => {
        this.listen(button, "click", () => {
          this.handleShareOption(button.dataset.share);
        });
      });

      this.listen(document, "click", (event) => {
        if (this.el.shareMenu && !this.el.shareMenu.hidden &&
            !this.el.shareMenu.contains(event.target) &&
            !this.el.share?.contains(event.target)) {
          this.toggleShareMenu(false);
        }

        if (this.el.removalShareMenu && !this.el.removalShareMenu.hidden &&
            !this.el.removalShareMenu.contains(event.target) &&
            !this.el.removalShare?.contains(event.target)) {
          this.toggleRemovalShareMenu(false);
        }
      });

      this.loadRoot();
    }
  }

  function resultValue(value) {
    return value == null || value === "" ? "—" : String(value);
  }

  function rootsIn(scope) {
    return [...(scope.matches?.('[data-nvs-tool="torque-basic"]') ? [scope] : []), ...scope.querySelectorAll('[data-nvs-tool="torque-basic"]')];
  }

  function boot(scope = document) {
    rootsIn(scope).forEach(root => {
      if (instances.has(root)) return;
      const instance = new NvsTorqueBasic(root);
      instances.set(root, instance);
      root.dataset.nvsToolBound = "true";
      instance.init();
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => boot(), { once: true });
  else boot();
  document.addEventListener("shopify:section:load", event => boot(event.target));
  document.addEventListener("shopify:section:unload", event => {
    rootsIn(event.target).forEach(root => { instances.get(root)?.destroy(); instances.delete(root); });
  });
})();